import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CheckCircle2, ChevronDown, ChevronRight, Search, SearchX } from 'lucide-react'
import { EmptyState } from '../radix'
import { AdminEnhancedPagination } from './AdminEnhancedPagination'
import { AdminSelect } from './AdminSelect'
import { OrdersTableSkeleton } from './OrdersTableSkeleton'
import { KindChip, NonSupplyScannerChip, SupplierChangeMoney } from './SupplierChangeParts'
import { useSupplierChangesPage, useSupplierChangesSummary } from '../../auth/hooks/useSupplierChanges'
import { useFoldedSuppliers } from '../../hooks/useFoldedSuppliers'
import { formatPoundsDifference } from '../../utils/currency'
import { splitDateTime } from '../../utils/datetime'
import type { SupplierChangeGroup, SupplierChangeOrder } from '../../auth/types'
import {
  FOCUS_RING,
  QUIET_BUTTON,
  RISK_BADGE,
  SUPPLIER_CHANGE_TIP,
  SUPPLIER_CHANGES_DEFAULT_PAGE_SIZE,
  SUPPLIER_CHANGES_SEARCH_DEBOUNCE_MS,
} from '../../constants/supplierChanges'

// The Orders page "Supplier changes to review" tab (PO-008), laid out as the
// mock's Option J: one row per order with supplier changes, grouped under its
// supplier (the supplier waiting longest first), then the order waiting
// longest first. Each supplier's heading folds and carries its orders, open
// changes and the supplier's change to the total. Read-only (Phase 1): a row
// opens the order. One page bar under the list, as the mock's Option D;
// a supplier's heading repeats when its orders carry on to the next page.

const ALL_SUPPLIERS = 'all'
const COLUMN_COUNT = 7
// Option J's header strip: light grey, small grey headings.
const TH = 'whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-gray-600'
// The whole row is one link: the order number stretches over it, so a click
// anywhere opens the order and "Open in new tab" works.
const ROW_LINK = 'group relative border-b border-gray-100 align-top transition-colors last:border-b-0 hover:bg-gray-50'
const ROW_ANCHOR =
  "font-semibold text-gray-900 underline-offset-2 group-hover:underline focus-visible:outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:rounded-sm focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-brand-primary"

export function SupplierChangesTab({ onShowAllOrders }: { onShowAllOrders?: () => void }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [search, setSearch] = useState('')
  const [supplierFilter, setSupplierFilter] = useState(ALL_SUPPLIERS)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(SUPPLIER_CHANGES_DEFAULT_PAGE_SIZE)
  const { folded, toggle } = useFoldedSuppliers()

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchQuery.trim())
      setCurrentPage(1)
    }, SUPPLIER_CHANGES_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // The page of orders, and the headings (every supplier's totals) over them.
  // With all suppliers shown, the page already carries the headings, so they
  // aren't asked for twice; with one supplier picked, the page doesn't, so
  // they come from the summary (which also lists every supplier for the
  // dropdown).
  const isAllSuppliers = supplierFilter === ALL_SUPPLIERS
  const summary = useSupplierChangesSummary(search, !isAllSuppliers)
  const pageQuery = useSupplierChangesPage({
    search: search || undefined,
    supplierId: isAllSuppliers ? null : Number(supplierFilter),
    page: currentPage,
    pageSize: itemsPerPage,
  })
  const groups = (isAllSuppliers ? pageQuery.data?.suppliers : summary.data?.suppliers) ?? []
  const orders = pageQuery.data?.results ?? []
  const totalItems = pageQuery.data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const error = summary.error ?? pageQuery.error
  const isLoading = summary.isLoading || pageQuery.isLoading
  const hasFilters = search !== '' || supplierFilter !== ALL_SUPPLIERS
  const pickSupplier = (value: string) => {
    setSupplierFilter(value)
    setCurrentPage(1)
  }
  const clearFilters = () => {
    setSearchQuery('')
    setSearch('')
    pickSupplier(ALL_SUPPLIERS)
  }

  return (
    <div className="space-y-3">
      {/* Option J's search row: search, then the supplier filter. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full flex-none sm:w-80">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" />
          <label htmlFor="review-search" className="sr-only">Search orders</label>
          <input id="review-search" type="search" placeholder="Order, engineer, tenant or branch" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" />
        </div>
        {groups.length > 1 && (
          <div role="group" aria-label="Supplier" className="w-48 min-w-0">
            <AdminSelect size="sm" value={supplierFilter} onChange={pickSupplier} options={[{ value: ALL_SUPPLIERS, label: 'All suppliers' }, ...groups.map((group) => ({ value: String(group.supplier_id), label: group.supplier_name ?? 'Unknown supplier' }))]} />
          </div>
        )}
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <h3 className="mb-2 font-bold text-red-900">Failed to load supplier changes</h3>
          <p className="text-sm text-red-700">{error.message || 'Please try again later'}</p>
        </div>
      ) : isLoading ? (
        <OrdersTableSkeleton />
      ) : orders.length === 0 && !hasFilters ? (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <EmptyState
            icon={<CheckCircle2 className="h-10 w-10 text-gray-400" aria-hidden="true" />}
            title="Nothing to review"
            description="Every supplier change is decided."
            action={onShowAllOrders && <button type="button" onClick={onShowAllOrders} className={QUIET_BUTTON}>Go to all orders</button>}
          />
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-[16px] border-2 border-gray-200 bg-white">
          <EmptyState
            icon={<SearchX className="h-10 w-10 text-gray-400" aria-hidden="true" />}
            title="No orders match your filters"
            description="Try adjusting your search or filters."
            action={<button type="button" onClick={clearFilters} className={QUIET_BUTTON}>Clear filters</button>}
          />
        </div>
      ) : (
        <>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Orders with supplier changes to review">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th scope="col" className={TH}>Order</th>
                  <th scope="col" className={TH}>Engineer</th>
                  <th scope="col" className={`${TH} w-full`}>What changed</th>
                  <th scope="col" className={TH}>Risk</th>
                  <th scope="col" className={TH}>Open</th>
                  <th scope="col" className={`${TH} text-right`} title={SUPPLIER_CHANGE_TIP}>Supplier&apos;s change</th>
                  <th scope="col" className={TH}>Waiting since</th>
                </tr>
              </thead>
              {pageSections(orders).map((section) => {
                const foldKey = String(section.supplierId)
                const group = groups.find((candidate) => candidate.supplier_id === section.supplierId)
                return <SupplierSection key={foldKey} group={group} supplierName={section.orders[0].supplier_name} orders={section.orders} isFolded={folded.has(foldKey)} onToggle={() => toggle(foldKey)} />
              })}
            </table>
          </div>
        </div>
        <AdminEnhancedPagination currentPage={Math.min(currentPage, totalPages)} totalPages={totalPages} itemsPerPage={itemsPerPage} totalItems={totalItems} onPageChange={setCurrentPage} onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1) }} />
        </>
      )}
    </div>
  )
}

/** The page's orders split into runs of one supplier, in list order. */
function pageSections(orders: SupplierChangeOrder[]): { supplierId: number | null; orders: SupplierChangeOrder[] }[] {
  const sections: { supplierId: number | null; orders: SupplierChangeOrder[] }[] = []
  for (const order of orders) {
    const last = sections[sections.length - 1]
    if (last && last.supplierId === order.supplier_id) last.orders.push(order)
    else sections.push({ supplierId: order.supplier_id, orders: [order] })
  }
  return sections
}

/** A supplier's heading that folds (totals for all its orders), then its orders on this page. */
function SupplierSection({ group, supplierName, orders, isFolded, onToggle }: { group: SupplierChangeGroup | undefined; supplierName: string | null; orders: SupplierChangeOrder[]; isFolded: boolean; onToggle: () => void }) {
  const { tenantSlug } = useParams()
  const net = group ? parseFloat(group.supplier_change) : 0
  return (
    <tbody>
      <tr className="border-b border-gray-100 bg-gray-50/70">
        <th scope="colgroup" colSpan={COLUMN_COUNT} className="p-0 text-left">
          <button type="button" aria-expanded={!isFolded} onClick={onToggle} className={`flex min-h-9 w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100 ${FOCUS_RING} focus-visible:ring-inset`}>
            {isFolded ? <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden="true" />}
            <span className="font-semibold text-gray-900">
              {group?.supplier_name ?? supplierName ?? 'Unknown supplier'}
              {group && ` · ${group.order_count} order${group.order_count === 1 ? '' : 's'}`}
            </span>
            {group && (
              <span className="tabular-nums">
                {' · '}
                {group.open_count} open · supplier&apos;s change {net === 0 ? 'none' : formatPoundsDifference(net)}
              </span>
            )}
          </button>
        </th>
      </tr>
      {!isFolded && orders.map((order) => <OrderRow key={order.id} order={order} href={`/${tenantSlug}/orders/${order.id}/supplier-changes`} />)}
    </tbody>
  )
}

function OrderRow({ order, href }: { order: SupplierChangeOrder; href: string }) {
  const placed = splitDateTime(order.created_at)
  const waiting = order.oldest_change_at ? splitDateTime(order.oldest_change_at) : { date: '', time: '' }
  return (
    <tr className={ROW_LINK}>
      <td className="whitespace-nowrap px-4 py-3">
        <Link to={href} className={ROW_ANCHOR}>{order.order_number}</Link>
        <div className="text-xs text-gray-500">Placed {placed.date}</div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <div className="text-gray-900">{order.engineer_name}</div>
        <div className="text-xs text-gray-500">
          {order.tenant_name}{order.branch_name ? ` · ${order.branch_name} branch` : ''}
        </div>
      </td>
      <td className="px-4 py-3">
        <KindChips order={order} />
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <RiskBadge order={order} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums text-gray-900">{order.changed_line_count} open</td>
      <td className="px-4 py-3 text-right">
        <div className="whitespace-nowrap font-semibold tabular-nums text-gray-900">
          <SupplierChangeMoney amount={order.supplier_change} />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="whitespace-nowrap text-gray-900">{waiting.date}</div>
        <div className="text-xs text-gray-500">{waiting.time}</div>
      </td>
    </tr>
  )
}

/** High or low risk of the order's changes (PO-008 AC2; not in Option J). */
function RiskBadge({ order }: { order: SupplierChangeOrder }) {
  const risk = RISK_BADGE[order.line_review_status]
  if (!risk) return null
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${risk.className}`}>{risk.label}</span>
}

/** What changed on an order, each kind once with how many (PO-008 AC2). */
function KindChips({ order }: { order: SupplierChangeOrder }) {
  return (
    <span className="flex flex-wrap gap-1">
      {order.change_kinds.map(({ kind, count }) => <KindChip key={kind} kind={kind} count={count} />)}
      {order.non_catalogue_count > 0 && <NonSupplyScannerChip count={order.non_catalogue_count} />}
    </span>
  )
}
